// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Severity } from '../../types/alarm';
import { useAlarms } from '../../hooks/useAlarms';
import { useAuth } from '../../hooks/useAuth';
import { useActiveSite } from '../../hooks/useActiveSite';
import { STATIC_SITES } from '../../config/sites';
import { PageHead, Loading } from '../../components/shell/ScreenHelpers';

const SEVERITIES: Severity[] = ['P1', 'P2', 'P3', 'P4'];

type SevCounts = Record<Severity, number>;

interface FleetSite {
  siteId: string;
  label: string;
}

function emptyCounts(): SevCounts {
  return { P1: 0, P2: 0, P3: 0, P4: 0 };
}

export default function FleetOverview() {
  const navigate = useNavigate();
  const { alarms, loading } = useAlarms({});
  const { sites } = useAuth();
  const { setSite } = useActiveSite();

  // Drill into a site: make it the active site (so the rail shows Site, not
  // Fleet) and open its Knowledge Graph.
  const openSite = (siteId: string) => {
    setSite(siteId);
    navigate(`/site/${siteId}/kg`);
  };

  // Fleet sites come from OMS / the /me response (useAuth().sites), with the
  // static definitions as a dev fallback — never a hardcoded list.
  const fleetSites: FleetSite[] = useMemo(() => {
    if (sites.length > 0) {
      return sites.map(s => ({ siteId: s.site_id, label: s.label }));
    }
    return STATIC_SITES.map(s => ({ siteId: s.id, label: s.label }));
  }, [sites]);

  const bySite = useMemo(() => {
    const acc: Record<string, SevCounts> = {};
    for (const site of fleetSites) {
      acc[site.siteId] = emptyCounts();
    }
    for (const alarm of alarms) {
      const counts = acc[alarm.siteId];
      if (counts) counts[alarm.sev] += 1;
    }
    return acc;
  }, [alarms, fleetSites]);

  const kpis = useMemo(() => {
    let p1 = 0;
    let p2 = 0;
    for (const counts of Object.values(bySite)) {
      p1 += counts.P1;
      p2 += counts.P2;
    }
    return {
      sites: fleetSites.length,
      p1,
      p2,
      open: alarms.length,
    };
  }, [bySite, alarms, fleetSites]);

  if (loading) {
    return (
      <div className="hs-page">
        <PageHead title="Fleet overview" dateline="All regions · live" />
        <Loading />
      </div>
    );
  }

  const stat = (label: string, value: number, color?: string) => (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 120,
        background: 'var(--layer-01)',
        border: '1px solid var(--border-subtle)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span style={{ fontSize: 26, fontWeight: 400, color: color ?? 'var(--text-primary)' }}>
        {value}
      </span>
      <span
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          color: 'var(--text-helper)',
        }}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div className="hs-page">
      <PageHead title="Fleet overview" dateline="All regions · live" />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {stat('Sites', kpis.sites)}
        {stat('P1 critical', kpis.p1, 'var(--sev1)')}
        {stat('P2 high', kpis.p2, 'var(--sev2)')}
        {stat('Open alarms', kpis.open)}
      </div>

      <div className="hs-tiles">
        {fleetSites.map(site => {
          const counts = bySite[site.siteId] ?? emptyCounts();
          return (
            <button
              key={site.siteId}
              type="button"
              className="hs-card"
              onClick={() => openSite(site.siteId)}
            >
              <div className="hs-card-asset">{site.label}</div>
              <div className="hs-card-row">
                {SEVERITIES.map(sev => (
                  <span key={sev} className={`hs-sev hs-sev-${sev}`}>
                    <span className="dot" />
                    {sev} {counts[sev]}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
